import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Building2, Sprout, Handshake, ArrowUpRight, Mail, Globe, User, MapPin } from "lucide-react";
import { toast } from "sonner";
import { CONTACT } from "@/constants/testIds";
import SectionLabel from "@/components/SectionLabel";
import usePageMeta from "@/hooks/usePageMeta";
import { buildPilotMailto, buildDeelnemerMailto } from "@/lib/mailto";

const pathways = [
  {
    id: CONTACT.cardGemeente,
    icon: Building2,
    label: "Gemeente",
    title: "Wilt u ontdekken wat LevelLeven kan betekenen voor uw inwoners?",
    cta: "Plan een kennismaking",
    role: "gemeente",
  },
  {
    id: CONTACT.cardDeelnemer,
    icon: Sprout,
    label: "Deelnemer",
    title: "Wil je weer in beweging? Wij lopen graag een stukje met je mee.",
    cta: "Meld je aan",
    role: "deelnemer",
  },
  {
    id: CONTACT.cardPartner,
    icon: Handshake,
    label: "Verwijzer of partner",
    title: "Werkt u bij een wijkteam, GGZ of welzijnsorganisatie? Laten we kennismaken.",
    cta: "Laten we praten",
    role: "partner",
  },
];

// Uitgebreide rol-chips inclusief deelnemer-varianten (zichtbaar in het formulier).
const roleOptions = [
  { role: "gemeente", label: "Gemeente", icon: Building2 },
  { role: "deelnemer", label: "Deelnemer", icon: Sprout },
  { role: "deelnemer-particulier", label: "Zonder uitkering", icon: User },
  { role: "wachtlijst", label: "Wachtlijst", icon: MapPin },
  { role: "partner", label: "Verwijzer / partner", icon: Handshake },
];

export default function Contact() {
  usePageMeta({
    title: "Contact, LevelLeven",
    description: "Neem contact op voor een pilotgesprek of om je aan te melden.",
  });
  const [form, setForm] = useState({ name: "", email: "", role: "gemeente", message: "" });
  const [honey, setHoney] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchParams] = useSearchParams();

  // Als er ?rol= in de URL staat, pre-selecteren en naar het formulier scrollen
  useEffect(() => {
    const rol = searchParams.get("rol");
    if (rol && roleOptions.some((r) => r.role === rol)) {
      setForm((f) => ({ ...f, role: rol }));
      setTimeout(() => {
        document.getElementById("contact-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
    }
  }, [searchParams]);

  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast("Vul alle velden in", {
        description: "Zonder naam, e-mail en bericht kunnen we niet reageren.",
      });
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, website: honey }),
      });
      if (res.status === 429) {
        toast("Even geduld", {
          description: "Te veel berichten in korte tijd. Probeer het later opnieuw.",
        });
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Verzenden mislukt");
      }
      setSubmitted(true);
      toast("Bedankt voor je bericht", {
        description: "We nemen binnen 2 werkdagen contact met je op.",
      });
      setForm({ name: "", email: "", role: form.role, message: "" });
    } catch (err) {
      toast("Er ging iets mis", {
        description:
          "Probeer het later opnieuw of mail rechtstreeks naar info@levelleven.nl.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* HERO */}
      <section className="py-16 sm:py-24">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl reveal">
            <SectionLabel>Contact</SectionLabel>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-[#1F5132] font-light">
              Klaar voor een{" "}
              <span className="italic text-[#E06B3F]">volgende stap?</span>
            </h1>
            <p className="mt-8 text-lg text-[#1F5132] font-light leading-relaxed">
              Een pilot is haalbaar binnen één begrotingsjaar. Voor deelnemers
              loopt het traject via de gemeente, wij helpen u graag bij de
              juiste route.
            </p>
          </div>
        </div>
      </section>

      {/* PATHWAYS */}
      <section className="pb-16">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {pathways.map((p, i) => {
              const Icon = p.icon;
              return (
                <button
                  key={p.id}
                  data-testid={p.id}
                  onClick={() => {
                    setForm((f) => ({ ...f, role: p.role }));
                    document.getElementById("contact-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  className="reveal text-left bg-[#F0EAD8] hover:bg-[#1F5132] hover:text-[#FAF7F1] rounded-2xl p-8 transition-colors duration-300 group"
                  style={{ transitionDelay: `${i * 70}ms` }}
                >
                  <div className="w-12 h-12 rounded-full bg-[#E06B3F]/10 text-[#E06B3F] group-hover:bg-[#E06B3F] group-hover:text-white flex items-center justify-center mb-6 transition-colors">
                    <Icon size={20} />
                  </div>
                  <p className="text-xs uppercase tracking-[0.25em] text-[#E06B3F] mb-4 group-hover:text-[#D19B72]">
                    {p.label}
                  </p>
                  <p className="font-heading text-2xl leading-snug font-light">{p.title}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-sm">
                    {p.cta}
                    <ArrowUpRight size={16} />
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* FORM */}
      <section id="contact-form" className="py-24 sm:py-32 bg-[#F0EAD8]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-5 reveal">
              <SectionLabel>Schrijf ons</SectionLabel>
              <h2 className="font-heading text-4xl sm:text-5xl leading-tight tracking-tight text-[#1F5132] font-light">
                Vertel kort waar het over gaat.
              </h2>
              <p className="mt-6 text-[#1F5132] font-light leading-relaxed">
                Wij reageren persoonlijk, geen automatische antwoorden,
                geen trechters.
              </p>

              <div className="mt-10 space-y-4 text-[#1F5132]">
                <a href="mailto:info@levelleven.nl" className="flex items-center gap-3 link-underline">
                  <Mail size={18} className="text-[#E06B3F]" />
                  info@levelleven.nl
                </a>
                <a href="https://levelleven.nl" className="flex items-center gap-3 link-underline">
                  <Globe size={18} className="text-[#E06B3F]" />
                  levelleven.nl
                </a>
              </div>

              <div className="mt-10 rounded-2xl border border-[#1F5132]/10 bg-[#FAF7F1] p-5">
                <p className="text-xs uppercase tracking-[0.25em] text-[#E06B3F] mb-3">
                  Liever direct mailen?
                </p>
                <p className="text-sm text-[#1F5132] mb-4 leading-relaxed">
                  Prefilled berichten voor snel contact, opent je eigen
                  mailclient.
                </p>
                <div className="flex flex-col gap-2">
                  <a
                    data-testid="contact-mailto-pilot"
                    href={buildPilotMailto()}
                    className="inline-flex items-center justify-between gap-3 rounded-full bg-[#1F5132] text-[#FAF7F1] hover:bg-[#E06B3F] pl-5 pr-2 py-2 transition-colors text-sm"
                  >
                    Pilotgesprek voor gemeente
                    <span className="bg-[#E06B3F] text-white w-8 h-8 rounded-full flex items-center justify-center">
                      <ArrowUpRight size={14} />
                    </span>
                  </a>
                  <a
                    data-testid="contact-mailto-deelnemer"
                    href={buildDeelnemerMailto()}
                    className="inline-flex items-center justify-between gap-3 rounded-full border border-[#1F5132] text-[#1F5132] hover:bg-[#1F5132] hover:text-[#FAF7F1] pl-5 pr-2 py-2 transition-colors text-sm"
                  >
                    Aanmelden als deelnemer
                    <span className="border border-current w-8 h-8 rounded-full flex items-center justify-center">
                      <ArrowUpRight size={14} />
                    </span>
                  </a>
                </div>
              </div>

              <p className="mt-10 text-xs uppercase tracking-[0.25em] text-[#3E4A42]">
                Doorlooptijd eerste contact → pilotstart
              </p>
              <p className="mt-2 font-heading text-3xl text-[#1F5132] font-light">
                6 tot 10 weken
              </p>
            </div>

            <div className="lg:col-span-7 reveal">
              {submitted ? (
                <div className="bg-[#FAF7F1] rounded-3xl p-10 sm:p-14 border border-[#1F5132]/10">
                  <p className="text-xs uppercase tracking-[0.25em] text-[#E06B3F] mb-4">
                    Bericht ontvangen
                  </p>
                  <p className="font-heading text-3xl sm:text-4xl text-[#1F5132] font-light leading-snug">
                    Dankjewel.{" "}
                    <span className="italic text-[#E06B3F]">We laten van ons horen.</span>
                  </p>
                  <p className="mt-6 text-[#1F5132] font-light">
                    Meestal reageren we binnen 2 werkdagen, altijd persoonlijk.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="mt-8 text-[#E06B3F] hover:text-[#8C4A31] link-underline"
                  >
                    Nog een bericht sturen
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="bg-[#FAF7F1] rounded-3xl p-8 sm:p-10 border border-[#1F5132]/10 space-y-6"
                >
                  <div>
                    <label className="block text-xs uppercase tracking-[0.25em] text-[#3E4A42] mb-2">
                      Ik ben een
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {roleOptions.map((p) => (
                        <button
                          type="button"
                          key={p.role}
                          data-testid={`${CONTACT.formRole}-${p.role}`}
                          onClick={() => setForm((f) => ({ ...f, role: p.role }))}
                          className={`rounded-full py-2 px-3.5 text-sm transition-colors border ${
                            form.role === p.role
                              ? "bg-[#1F5132] text-[#FAF7F1] border-[#1F5132]"
                              : "bg-transparent border-[#1F5132]/10 text-[#1F5132] hover:border-[#1F5132]"
                          }`}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="name" className="block text-xs uppercase tracking-[0.25em] text-[#3E4A42] mb-2">
                      Naam
                    </label>
                    <input
                      id="name"
                      data-testid={CONTACT.formName}
                      type="text"
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      className="w-full bg-transparent border-b border-[#1F5132]/10 focus:border-[#1F5132] outline-none py-3 text-lg text-[#1F5132] placeholder-[#3E4A42] transition-colors"
                      placeholder="Jouw naam"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-xs uppercase tracking-[0.25em] text-[#3E4A42] mb-2">
                      E-mail
                    </label>
                    <input
                      id="email"
                      data-testid={CONTACT.formEmail}
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                      className="w-full bg-transparent border-b border-[#1F5132]/10 focus:border-[#1F5132] outline-none py-3 text-lg text-[#1F5132] placeholder-[#3E4A42] transition-colors"
                      placeholder="naam@voorbeeld.nl"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-xs uppercase tracking-[0.25em] text-[#3E4A42] mb-2">
                      Bericht
                    </label>
                    <textarea
                      id="message"
                      data-testid={CONTACT.formMessage}
                      rows={5}
                      value={form.message}
                      onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                      className="w-full bg-transparent border-b border-[#1F5132]/10 focus:border-[#1F5132] outline-none py-3 text-lg text-[#1F5132] placeholder-[#3E4A42] transition-colors resize-none"
                      placeholder="Waar gaat het over?"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid={CONTACT.formSubmit}
                    className="group inline-flex items-center gap-4 bg-[#E06B3F] hover:bg-[#1F5132] disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-full pl-8 pr-2 py-2 transition-colors duration-300"
                  >
                    <span className="text-base font-medium">
                      {submitting ? "Bezig met versturen…" : "Verstuur bericht"}
                    </span>
                    <span className="bg-[#1F5132] group-hover:bg-[#E06B3F] text-white w-12 h-12 rounded-full flex items-center justify-center transition-colors">
                      <ArrowUpRight size={20} />
                    </span>
                  </button>
                  {/* Honeypot, verborgen voor mensen */}
                  <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={honey}
                    onChange={(e) => setHoney(e.target.value)}
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      left: "-9999px",
                      width: "1px",
                      height: "1px",
                      opacity: 0,
                    }}
                  />
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
